import { QueryClient, QueryCache, MutationCache, QueryFunction } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { queryKeyToPath } from "./query-key-path";

export { queryKeyToPath } from "./query-key-path";

const SERVER_RESTARTING_MESSAGE = "Server is restarting — please try again in a moment.";

function isRestartError(err: unknown): boolean {
  return err instanceof Error && err.message === SERVER_RESTARTING_MESSAGE;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
  const contentType = res.headers.get("Content-Type") ?? "";
  if (contentType.startsWith("text/html")) {
    throw new Error(SERVER_RESTARTING_MESSAGE);
  }
}

// The server endpoint that trades a still-valid 30-day refresh-token
// cookie for a fresh 1-hour access-token cookie. Hitting it must never
// itself be wrapped in the refresh-and-retry logic below (that would
// recurse forever on a truly-expired session).
const REFRESH_URL = "/api/auth/refresh";

// Single-flight access-token refresh. A page that has sat idle past the
// 1-hour access-token lifetime will often fire many API calls at once
// (queries + a mutation), and each comes back 401 together. We must
// refresh exactly once for that whole wave, not once per call, so all
// callers share one in-flight promise. Resolves `true` when the session
// was renewed, `false` when the refresh token is also gone/expired (a
// genuinely signed-out session). The promise is cleared once settled so
// a later expiry can trigger a fresh refresh.
let _refreshPromise: Promise<boolean> | null = null;
function refreshSession(): Promise<boolean> {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const res = await fetch(REFRESH_URL, {
        method: "POST",
        credentials: "include",
      });
      return res.ok;
    } catch {
      return false;
    }
  })();
  void _refreshPromise.finally(() => {
    _refreshPromise = null;
  });
  return _refreshPromise;
}

function rawFetch(method: string, url: string, data?: unknown): Promise<Response> {
  return fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });
}

// Perform a fetch and, if it comes back 401 because the access token has
// quietly expired, transparently refresh the session once and retry the
// original request. A 401 means the server rejected the call *before*
// running the route handler, so retrying any method (incl. POST) is safe
// — no risk of double-submission. The refresh call itself is excluded so
// it can never recurse.
async function fetchWithRefresh(method: string, url: string, data?: unknown): Promise<Response> {
  const res = await rawFetch(method, url, data);
  if (res.status !== 401 || url === REFRESH_URL) return res;
  const refreshed = await refreshSession();
  if (!refreshed) return res;
  return rawFetch(method, url, data);
}

async function doFetch(method: string, url: string, data?: unknown): Promise<Response> {
  const res = await fetchWithRefresh(method, url, data);
  await throwIfResNotOk(res);
  return res;
}

/**
 * Make an authenticated API request. When the dev server is restarting it
 * returns an HTML page instead of JSON; in that case the server provably
 * never executed the route handler, so it is safe to retry any method
 * without risk of double-submission.
 *
 * The retry waits ~2 s before the second attempt.  Pass
 * `{ retryOnRestart: false }` to opt out for any call where you need
 * strictly-once semantics even during a restart.
 */
export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: { retryOnRestart?: boolean },
): Promise<Response> {
  const shouldRetry = options?.retryOnRestart ?? true;

  try {
    return await doFetch(method, url, data);
  } catch (err) {
    if (shouldRetry && isRestartError(err)) {
      await new Promise<void>((resolve) => setTimeout(resolve, 2000));
      return await doFetch(method, url, data);
    }
    throw err;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";

export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetchWithRefresh("GET", queryKeyToPath(queryKey));

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

// Server error responses arrive as `${status}: ${body}` where the body is
// often a JSON envelope like `{"error":"title: must be at most 200
// characters","issues":[...]}`. Surfacing the raw JSON in a toast buries the
// human-readable sentence in braces and field metadata. Pull out just the
// `error` string when the body parses as a JSON object that has one; fall
// back to the raw text for plain-string bodies (so existing friendly
// messages are unchanged).
function extractErrorBody(text: string): string {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return text;
  try {
    const parsed = JSON.parse(trimmed) as {
      error?: unknown;
      message?: unknown;
      details?: unknown;
      htmlErrors?: unknown;
      contentErrors?: unknown;
    };
    if (parsed && typeof parsed === "object") {
      // Zod registration errors: { error: "Invalid registration data", details: [...] }
      if (Array.isArray(parsed.details) && parsed.details.length > 0) {
        const first = parsed.details[0] as { message?: unknown; path?: unknown };
        if (typeof first?.message === "string" && first.message.trim()) {
          const path = Array.isArray(first.path) && first.path.length > 0
            ? `${first.path.join(".")}: `
            : "";
          return `${path}${first.message}`;
        }
      }
      if (typeof parsed.message === "string" && parsed.message.trim()) {
        return parsed.message;
      }
      if (typeof parsed.error === "string") {
        if (Array.isArray(parsed.htmlErrors) && parsed.htmlErrors.length > 0) {
          const bits = parsed.htmlErrors
            .filter((e): e is string => typeof e === "string")
            .slice(0, 3);
          if (bits.length > 0) return `${parsed.error} ${bits.join(" ")}`;
        }
        return parsed.error;
      }
    }
  } catch {
    // Not JSON — fall through and return the original text.
  }
  return text;
}

export function getReadableErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const msg = error.message;
    if (msg.startsWith("401:")) return "Your session has expired. Please sign in again.";
    if (msg.startsWith("403:")) return "You don't have permission to access this resource.";
    if (msg.startsWith("404:")) return "The requested content could not be found.";
    if (msg.startsWith("429:")) return "Too many requests. Please wait a moment and try again.";
    if (msg.startsWith("500:") || msg.startsWith("502:") || msg.startsWith("503:"))
      return "Our servers are having trouble. Please try again in a moment.";
    const cleaned = msg.replace(/^\d{3}:\s*/, "");
    if (!cleaned) return "An unexpected error occurred.";
    return extractErrorBody(cleaned) || "An unexpected error occurred.";
  }
  return "An unexpected error occurred.";
}

/**
 * Centralized invalidator for every cache that derives from a project's
 * documents. Includes the investor deal-room key and agent project doc
 * key so admin uploads propagate to downstream views in the same tab.
 */
export function invalidateProjectDocuments(projectId: string) {
  queryClient.invalidateQueries({ queryKey: ["/api/admin/pipeline/projects", projectId, "all-documents"] });
  queryClient.invalidateQueries({ queryKey: ["/api/admin/pipeline/projects", projectId] });
  queryClient.invalidateQueries({ queryKey: ["/api/admin/projects", projectId, "documents"] });
  queryClient.invalidateQueries({ queryKey: ["/api/admin/project-deal-room-summary"] });
  queryClient.invalidateQueries({ queryKey: ["/api/deal-room", projectId, "documents"] });
  queryClient.invalidateQueries({ queryKey: ["/api/agent/projects", projectId, "documents"] });
}

/**
 * Entity vault master data (EIN, formation date, address, owners) and
 * documents feed Section A readiness on every linked pipeline project.
 * Those project queries use staleTime: Infinity, so a vault save must
 * invalidate them or the project legal-entities card keeps the old row
 * (EIN stays "—" after it was saved on the entity page).
 */
export function invalidateEntityDocuments(entityId?: string) {
  if (entityId) {
    queryClient.invalidateQueries({ queryKey: ["/api/admin/entities", entityId] });
  }
  queryClient.invalidateQueries({ queryKey: ["/api/admin/entities"] });
  queryClient.invalidateQueries({ queryKey: ["/api/admin/pipeline/projects"] });
}

/**
 * Invalidate the agent CRM caches that derive from admin-side investor
 * mutations (pipeline stage, referral-agent assignment, source-of-funds,
 * accreditation approval).
 */
export function invalidateInvestorDownstream(investorId?: string) {
  queryClient.invalidateQueries({ queryKey: ["/api/agent/investors"] });
  queryClient.invalidateQueries({ queryKey: ["/api/agent/dashboard"] });
  if (investorId) {
    queryClient.invalidateQueries({ queryKey: ["/api/agent/investors", investorId, "timeline"] });
  }
}

/**
 * Invalidate both the admin marketing list and the public landing
 * preview prefix so an admin previewing the public page sees fresh
 * hero/CTA copy after any edit, toggle, delete, or seed mutation.
 */
export function invalidateMarketingPages() {
  queryClient.invalidateQueries({ queryKey: ["/api/admin/marketing"] });
  queryClient.invalidateQueries({ queryKey: ["/api/marketing"] });
}

// Forward query/mutation failures to Sentry, dropping status codes
// that already drive UX (401 → login, 403, 404, 429).
const EXPECTED_STATUS_RE = /^(401|403|404|429):/;

function isExpectedHttpError(err: unknown): boolean {
  if (err instanceof Error) {
    if ((err as any).sentryExpected === true) return true;
    return EXPECTED_STATUS_RE.test(err.message);
  }
  return false;
}

/**
 * Mark an error as an expected, UX-handled outcome so the global
 * query/mutation error hooks do not report it to Sentry. Use this only
 * for *recognized* validation responses the calling code fully handles
 * (e.g. the underwriting approval-readiness 400) — never as a blanket
 * status-code suppression, which would hide real bugs.
 */
export function markErrorAsExpected<T extends Error>(err: T): T {
  (err as any).sentryExpected = true;
  return err;
}

// "Am I signed in?" probe queries. Every portal mounts one of these
// at the top of its tree and a 401 is the *normal* "not signed in yet"
// answer — not a session expiry. They must never trigger the hard
// login redirect below: an unauthenticated portal page (login,
// set-password, reset-password) would reload itself in an infinite
// strobe. Each portal's layout/page already handles the null identity
// with a single soft redirect via wouter.
//
// Rather than an explicit per-portal allowlist (which a new portal
// could forget to update, silently reintroducing the strobe), we match
// the shared "current identity" convention structurally: any key of the
// form `/api/<portal>/me`. This covers investor (`/api/auth/me`),
// partner/agent, vendor, sponsor — and any future portal — automatically.
// Note `/api/me/<something>` (e.g. `/api/me/referral-agent`) is a data
// endpoint, NOT an identity probe, and correctly does not match.
const AUTH_PROBE_KEY_RE = /^\/api\/[^/]+\/me$/;
function isAuthProbeQueryKey(key: string): boolean {
  return AUTH_PROBE_KEY_RE.test(key);
}

// When an API call returns 401 it means the session has expired (the
// access-token cookie is gone or invalid). Rather than showing a
// confusing "couldn't load" error, redirect the user to the appropriate
// login page. Debounced so that multiple simultaneous 401s from the same
// page only trigger one redirect.
let _loginRedirectScheduled = false;
function scheduleLoginRedirect(): void {
  if (_loginRedirectScheduled) return;
  // Never reload auth/apply surfaces — a 401 there is usually a failed
  // credential check (or "not signed in yet"). Reloading would wipe the
  // inline/toast error and look like a silent no-op.
  try {
    const path = window.location.pathname;
    if (
      path === "/login" ||
      path === "/register" ||
      path === "/logout" ||
      path === "/agents/apply" ||
      path === "/agent/apply" ||
      path === "/forgot-password" ||
      path === "/reset-password"
    ) {
      return;
    }
  } catch {
    // ignore
  }
  _loginRedirectScheduled = true;
  setTimeout(() => {
    // Task #1970 — universal login. Every portal's session expiry now
    // lands on the single `/login` page, which resolves the role on the
    // next sign-in. Portal-specific login pages were retired.
    window.location.href = "/login";
  }, 150);
}

// Task #1584 — Centralized handler for the partner holding-state
// 403. Any gated agent route returns `403 { error: "pending_approval" }`
// when the caller's firm has not been approved yet. From the client
// side this means our cached view of `/api/agent/me` is stale (the
// admin un-approved the partner between page loads, or the partner's
// session predates a status flip). We invalidate the agent/me cache
// so `useAgentAuth` re-reads `portalStatus`, which flips `isPending`
// in `AgentLayout` and swaps the gated page for the in-place
// `<PendingApprovalEmptyState>` empty state — without a hard redirect.
function isPendingApprovalError(error: unknown): boolean {
  return error instanceof Error
    && error.message.startsWith("403:")
    && error.message.includes("pending_approval");
}
function handlePendingApprovalError(): void {
  void queryClient.invalidateQueries({ queryKey: ["/api/agent/me"] });
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (error instanceof Error && error.message.startsWith("401:")) {
        // Auth-probe queries (/api/{auth,agent,vendor,sponsor}/me)
        // returning 401 are handled by their portal layout — don't
        // hard-redirect, and don't redirect on public pages where a 401
        // just means "not logged in yet". A hard redirect on an
        // unauthenticated portal page (login/set-password/reset-password)
        // would reload the page that issued the probe, creating an
        // infinite login strobe.
        const key = String(query.queryKey?.[0] ?? "");
        if (!isAuthProbeQueryKey(key)) {
          scheduleLoginRedirect();
        }
        return;
      }
      if (isPendingApprovalError(error)) {
        handlePendingApprovalError();
        return;
      }
      if (isExpectedHttpError(error)) return;
      Sentry.captureException(error, {
        tags: { kind: "react_query", queryKey: String(query.queryKey?.[0] ?? "unknown") },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (error instanceof Error && error.message.startsWith("401:")) {
        scheduleLoginRedirect();
        return;
      }
      if (isPendingApprovalError(error)) {
        handlePendingApprovalError();
        return;
      }
      if (isExpectedHttpError(error)) return;
      Sentry.captureException(error, {
        tags: { kind: "react_mutation", mutationKey: String(mutation.options.mutationKey?.[0] ?? "unknown") },
      });
    },
  }),
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: (failureCount, error) => {
        if (error instanceof Error) {
          const status = parseInt(error.message);
          if (status === 401 || status === 403 || status === 404) return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
    mutations: {
      retry: false,
    },
  },
});
